import React from "react";
import { Box, Typography, Grid, Paper, Stack, Chip } from "@mui/material";
import {
  FieldGoalPlayCall,
  FieldGoalPlayLog,
  PenaltyLog,
} from "../../../../types/LogTypes";
import { jerseyNumberNameDisplay } from "../../../../helpers/helpers";
import { PenaltyLogDetails } from "../Components/PenaltyLogDetails";

interface FieldGoalLogDetailsProps {
  playCall: FieldGoalPlayCall;
  playLog: FieldGoalPlayLog;
  penalties: PenaltyLog[];
}

export const FieldGoalLogDetails: React.FC<FieldGoalLogDetailsProps> = ({
  playCall,
  playLog,
  penalties,
}) => {
  const getResultColor = (result: "GOOD" | "MISSED" | "BLOCKED") => {
    switch (result) {
      case "GOOD":
        return "success";
      case "MISSED":
        return "error";
      case "BLOCKED":
        return "warning";
      default:
        return "primary";
    }
  };

  console.log("play call", playCall);
  console.log("play log", playLog);
  console.log("penalties", penalties);

  return (
    <Box sx={{ width: "100%" }}>
      <Grid container spacing={3}>
        {/* Play Call Section */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              height: "100%",
              backgroundColor: "background.paper",
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Play Call
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Personnel
                </Typography>
                <Typography variant="h6">
                  {playCall.personnel_group.name || ""}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Kick Type
                </Typography>
                <Typography variant="h6">
                  {playCall?.kick_type === "PAT" ? "Extra Point" : "Field Goal"}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Play Result Section */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              height: "100%",
              backgroundColor: "background.paper",
              borderRadius: 2,
              position: "relative",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Play Result
            </Typography>

            {playLog && (
              <Stack spacing={2}>
                {/* Kicker */}
                {playLog.kicker_roster_player && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Kicker
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {jerseyNumberNameDisplay(playLog.kicker_roster_player)}
                    </Typography>
                  </Box>
                )}

                {/* Field Goal Details */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {playLog.pat ? "Extra Point" : "Field Goal"} Details
                  </Typography>
                  <Typography variant="body1">
                    Distance: {playLog.field_goal_distance} yards
                  </Typography>
                  <Chip
                    label={playLog.field_goal_result}
                    color={getResultColor(playLog.field_goal_result)}
                    sx={{ mt: 1 }}
                  />
                </Box>

                {/* Turnover Section */}
                {(playLog.fumble || playLog.interception) && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.5,
                      backgroundColor: "error.main",
                      color: "error.contrastText",
                      borderRadius: 1,
                    }}
                  >
                    {playLog.fumble && (
                      <>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600 }}
                        >
                          FUMBLE
                        </Typography>
                        {playLog.fumble.fumble_roster_player && (
                          <Typography variant="body2">
                            Fumbled by:{" "}
                            {jerseyNumberNameDisplay(
                              playLog.fumble.fumble_roster_player
                            )}
                          </Typography>
                        )}
                        {playLog.fumble.fumble_recovery_roster_player && (
                          <Typography variant="body2">
                            Recovered by:{" "}
                            {jerseyNumberNameDisplay(
                              playLog.fumble.fumble_recovery_roster_player
                            )}
                          </Typography>
                        )}
                      </>
                    )}
                    {playLog.interception && (
                      <>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600 }}
                        >
                          INTERCEPTION
                        </Typography>
                        {playLog.interception.interception_roster_player && (
                          <Typography variant="body2">
                            Intercepted by:{" "}
                            {jerseyNumberNameDisplay(
                              playLog.interception.interception_roster_player
                            )}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                )}
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* Penalties Section */}
        <Grid item xs={12} md={4}>
          <PenaltyLogDetails penalties={penalties} />
        </Grid>
      </Grid>
    </Box>
  );
};
